import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { House, Menu, Plus, Wrench } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { ChatLanding } from "@/components/chat/ChatLanding";
import { ChatPulita } from "@/components/chat/ChatPulita";
import { MessageList } from "@/components/chat/MessageList";
import { Salvagente } from "@/components/chat/Salvagente";
import { ChatInput } from "@/components/chat/ChatInput";
import { DegradedBanner } from "@/components/chat/DegradedBanner";
import { MobileSidebarDrawer } from "@/components/layout/MobileSidebarDrawer";
import { InstallPromptBanner } from "@/components/InstallPromptBanner";
import { Button } from "@/components/ui/button";
import { useConversations, type ConversationRow } from "@/hooks/useConversations";
import { useMessages, messagesKey } from "@/hooks/useMessages";
import { useDemoMode } from "@/hooks/useSettings";
import { useKeyboardOpen } from "@/hooks/useKeyboardOpen";
import {
  ControlloNonRiuscito,
  ProfiloIncompleto,
  ProvaScaduta,
  PartitaIvaNonValida,
  QuotaEsaurita,
  ServeAccount,
  sendChatMessage,
} from "@/lib/chatClient";
import { statoAccount } from "@/lib/accesso";
import { BloccoProvaScaduta, BannerProva } from "@/components/chat/BloccoProvaScaduta";
import { useAuth } from "@/hooks/useAuth";
import {
  chatBloccataQui,
  consensoAiDato,
  consensoAiRichiesto,
  invitiAllAcquistoNellApp,
  registraConsensoAi,
} from "@/lib/iscrizione";
import { ConsensoAI } from "@/components/chat/ConsensoAI";
import type { ChatMessage } from "@/lib/types";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Cosa si scrive in chat quando il giorno e' finito o la prova e' scaduta.
 *
 * Il messaggio di merito arriva dal server, che sa in che stato e' l'account
 * (anonimo, in prova, attivo): qui si aggiunge solo il passo successivo giusto.
 * Un anonimo va invitato a iscriversi; un registrato va mandato alla richiesta
 * di sblocco - mandarlo alla pagina di registrazione, come si faceva prima, e'
 * il modo migliore per farlo andare via.
 */
function testoQuotaEsaurita(messaggio: string, registrato: boolean): string {
  // Sull'iPhone nessun invito a iscriversi o a chiedere l'attivazione
  // (Apple 3.1.1, 29/09): resta il messaggio del server, e basta.
  if (!invitiAllAcquistoNellApp()) return messaggio;
  // Nell'app nativa l'origine e' «localhost» (Capacitor): il link va al sito vero.
  const sito = Capacitor.isNativePlatform() ? "https://ai.datacomtecnologie.it" : window.location.origin;
  if (!registrato) {
    return `${messaggio}

Registra la tua impresa (servono i dati dell'azienda e la partita IVA): ${sito}/signup`;
  }
  // L'invito allo sblocco ha senso per chi e' in PROVA; chi e' gia' attivo e
  // tocca il tetto settimanale ha gia' l'account (e il testo del server lo
  // manda a Datacom per gli account aggiuntivi).
  return /prova/i.test(messaggio)
    ? `${messaggio}

Per l'account installatore: ${sito}/richiedi-accesso`
    : messaggio;
}

export default function Chat() {
  const { profile, user } = useAuth();
  // Lo stato dell'account guida popup e banner SENZA aspettare un 403: chi apre
  // l'app a prova finita vede subito il blocco, non dopo la prima domanda persa.
  // Sull'iPhone no: li' chi non ha licenza ha l'account gratuito, e il limite lo
  // mette il server (04/10, audit: fino alla 126 l'app si bloccava all'8° giorno).
  const accountStato = useMemo(() => statoAccount(profile), [profile]);
  const bloccata = chatBloccataQui(accountStato);
  const [popupScaduto, setPopupScaduto] = useState(false);
  useEffect(() => {
    if (bloccata) setPopupScaduto(true);
  }, [bloccata]);
  // iPhone: permesso esplicito per l'AI di terzi prima del primo uso (Apple 5.1.2(i)).
  const [consensoAi, setConsensoAi] = useState(() => consensoAiDato(user?.id));
  const [chiediConsenso, setChiediConsenso] = useState(false);
  useEffect(() => {
    const dato = consensoAiDato(user?.id);
    setConsensoAi(dato);
    if (consensoAiRichiesto() && user?.id && !dato) setChiediConsenso(true);
  }, [user?.id]);
  const senzaConsenso = consensoAiRichiesto() && !consensoAi;
  const navigate = useNavigate();
  const params = useParams();
  const [search, setSearch] = useSearchParams();
  const qc = useQueryClient();
  const {
    items: conversazioni,
    isLoading: conversazioniInCaricamento,
    create,
    get,
    updateDifyConversationId,
    touch,
    maybeAutoTitle,
  } = useConversations();
  const { demoMode } = useDemoMode();
  const keyboardOpen = useKeyboardOpen();
  const filter = "all" as const;

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState<ConversationRow | null>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  // Messaggio assistant in streaming (non ancora persistito su DB)
  const [streamingMsg, setStreamingMsg] = useState<ChatMessage | null>(null);
  // A quale conversazione appartiene la risposta che sta arrivando. Serve
  // perche' si puo' cambiare conversazione mentre il motore scrive: senza
  // questo, il testo comparirebbe sotto la conversazione sbagliata.
  const [streamingConvId, setStreamingConvId] = useState<string | null>(null);
  // Immagine allegata al prossimo messaggio (sessione corrente)
  const [image, setImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  // Mappa message_id → object URL immagine (sopravvive nella sessione corrente)
  const [userImages, setUserImages] = useState<Record<string, string>>({});
  const sendingRef = useRef(false);
  // L'invio in corso, per poterlo fermare. Senza questo, chi apriva una nuova
  // chat mentre il motore stava rispondendo si vedeva comparire la risposta
  // VECCHIA dentro la conversazione NUOVA: lo stream continuava a scrivere su
  // uno schermo che nel frattempo era cambiato.
  const invioRef = useRef<AbortController | null>(null);

  const fermaInvio = useCallback(() => {
    invioRef.current?.abort();
    invioRef.current = null;
    sendingRef.current = false;
    setLoading(false);
    setStreamingMsg(null);
    setStreamingConvId(null);
  }, []);

  // Allo smontaggio non deve restare niente in volo.
  useEffect(() => () => invioRef.current?.abort(), []);

  const conversationId = conversation?.id ?? null;
  const { messages: dbMessages, insertUserMessage, insertAssistantMessage } = useMessages(conversationId);

  /**
   * L'ULTIMA CONVERSAZIONE SI RIPRENDE, NON SI SUBISCE.
   *
   * Il 27/08 mattina, prima Luca di Datacom e poi Davide: «all'apertura ti
   * porta all'interno dell'ultima chat e non alla Home». Avevano ragione, ed
   * era colpa di una correzione fatta la notte prima: `/chat` senza id veniva
   * dirottato sull'ultima conversazione, appena la lista arrivava.
   *
   * L'errore stava nel presupposto. `/chat` senza id NON e' un foglio bianco
   * qualunque: e' la schermata iniziale dell'app - saluto, comunicazioni di
   * Datacom, strumenti, ultime domande. Dirottandola, chi apriva l'app non la
   * vedeva mai piu', e le comunicazioni che Umberto pubblica non le vedeva
   * nessuno.
   *
   * Il bisogno di partenza resta vero: chi riapre l'app in cantiere non deve
   * perdere il filo. Ma si serve con un invito, non con un dirottamento. Qui
   * sotto si calcola solo QUALE conversazione proporre; ad aprirla ci pensa
   * l'installatore con un tocco, dalla schermata iniziale.
   *
   * Solo le ultime 24 ore: piu' indietro non e' "dove ero rimasto", e' storia,
   * e la storia sta in Cronologia.
   */
  const ultimaDaRiprendere = useMemo(() => {
    if (conversazioniInCaricamento) return null;
    const ultima = conversazioni[0];          // gia' ordinate per updated_at
    if (!ultima) return null;
    const quando = new Date(ultima.updated_at).getTime();
    if (!Number.isFinite(quando)) return null;
    return Date.now() - quando < 24 * 60 * 60 * 1000 ? ultima : null;
  }, [conversazioni, conversazioniInCaricamento]);

  // Prefill da Welcome page (esempio cliccato)
  useEffect(() => {
    try {
      const pre = sessionStorage.getItem("datacom.chat.prefill");
      if (pre) {
        sessionStorage.removeItem("datacom.chat.prefill");
        setInput(pre);
      }
    } catch { /* niente prefill dalla pagina di benvenuto: si parte vuoti */ }
  }, []);

  // Carica conversazione da URL o gestisci /chat?new=1
  useEffect(() => {
    let cancelled = false;
    if (search.get("new") === "1") {
      fermaInvio();
      setConversation(null);
      setStreamingMsg(null);
      // Il parametro RESTA: e' quello che tiene la pagina pulita finche' non si
      // scrive. Prima veniva cancellato subito, e un istante dopo ricompariva
      // la schermata iniziale al posto del foglio bianco.
      if (params.id) navigate("/chat?new=1", { replace: true });
      return;
    }
    if (params.id) {
      void get(params.id).then((found) => {
        if (cancelled) return;
        if (found) setConversation(found);
        else navigate("/chat", { replace: true });
      });
    } else {
      setConversation(null);
      setStreamingMsg(null);
    }
    return () => {
      cancelled = true;
    };
  }, [params.id, search, setSearch, get, navigate, fermaInvio]);

  const messages = useMemo<ChatMessage[]>(() => {
    const base = dbMessages.map((m) =>
      userImages[m.id] ? { ...m, imageUrl: userImages[m.id] } : m,
    );
    const mostraStream = streamingMsg && streamingConvId === conversationId;
    return mostraStream ? [...base, streamingMsg] : base;
  }, [dbMessages, streamingMsg, streamingConvId, userImages, conversationId]);

  // Gestione preview URL immagine pendente
  const handleImageChange = useCallback((f: File | null) => {
    setImage(f);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * «Nuova chat» apre un foglio pulito, non la schermata iniziale.
   *
   * Davide, 27/08: «il tasto + puo' diventare davvero nuova chat, ma deve
   * portare a una pagina pulita, diversa dalla home». Prima finiva su `/chat`,
   * che e' la schermata iniziale: chi voleva partire da zero si ritrovava
   * comunicazioni, scorciatoie e il resto. Il `?new=1` resta nell'indirizzo -
   * e' lui a dire quale delle due schermate mostrare - e sparisce da solo alla
   * prima domanda, quando si passa a `/chat/<id>`.
   */
  const startNewChat = useCallback(() => {
    fermaInvio();
    setConversation(null);
    setStreamingMsg(null);
    setInput("");
    navigate("/chat?new=1", { replace: Boolean(params.id) });
  }, [navigate, params.id, fermaInvio]);

  const send = useCallback(
    async (text: string, attachedImage?: File | null) => {
      const message = text.trim();
      const imgToSend = attachedImage !== undefined ? attachedImage : image;
      if ((!message && !imgToSend) || sendingRef.current) return;
      // Anche i suggerimenti e la domanda del benvenuto passano da qui: senza
      // permesso per l'AI (iPhone) non parte niente, si richiede il permesso.
      if (consensoAiRichiesto() && !consensoAiDato(user?.id)) {
        setChiediConsenso(true);
        return;
      }
      sendingRef.current = true;
      invioRef.current?.abort();
      const controllore = new AbortController();
      invioRef.current = controllore;

      let conv = conversation;
      const isFirstUserInConv = (conv ? dbMessages : []).every((m) => m.role !== "user");
      try {
        if (!conv) {
          conv = await create();
          setConversation(conv);
          navigate(`/chat/${conv.id}`, { replace: true });
        }

        // 1) INSERT user message
        const insertedUser = await insertUserMessage(conv.id, message);
        if (imgToSend) {
          // Mantieni l'object URL già creato come preview, così non ricreo blob.
          const url = imagePreviewUrl ?? URL.createObjectURL(imgToSend);
          setUserImages((prev) => ({ ...prev, [insertedUser.id]: url }));
          // Sgancia preview senza revocare: l'URL ora vive in userImages.
          setImagePreviewUrl(null);
          setImage(null);
        }
        qc.invalidateQueries({ queryKey: messagesKey(conv.id) });

        // 2) Auto-title se è il primo user message
        if (isFirstUserInConv) {
          void maybeAutoTitle(conv.id, conv.title, message);
        }

        setInput("");
        setLoading(true);

        const assistantId = uid();
        const idConv = conv.id;
        const ensureStreaming = () => {
          setLoading(false);
          setStreamingConvId(idConv);
          setStreamingMsg((prev) =>
            prev ?? {
              id: assistantId,
              role: "assistant",
              content: "",
              createdAt: Date.now(),
            },
          );
        };

        const res = await sendChatMessage(
          {
            message,
            selectedKnowledgeFilter: filter,
            conversationId: conv.id,
            difyConversationId: conv.dify_conversation_id ?? "",
            // I marchi dell'installatore limitano il retrieval ai suoi manuali.
            brands: profile?.brands ?? [],
          },
          {
            demoMode,
            signal: controllore.signal,
            image: imgToSend ?? null,
            onToken: (chunk) => {
              ensureStreaming();
              setStreamingMsg((prev) =>
                prev ? { ...prev, content: prev.content + chunk } : prev,
              );
            },
            onMeta: (meta) => {
              ensureStreaming();
              setStreamingMsg((prev) =>
                prev
                  ? {
                      ...prev,
                      status: meta.status,
                      source: meta.source ?? prev.source,
                      citations: meta.citations ?? prev.citations,
                      messageId: meta.message_id ?? prev.messageId,
                      transparency: meta.transparency ?? prev.transparency,
                    }
                  : prev,
              );
            },
          },
        );

        // 3) INSERT messaggio assistant finale (una sola scrittura DB)
        await insertAssistantMessage(conv.id, {
          content: res.answer,
          status: res.status,
          source: res.source,
          citations: res.citations,
          dify_message_id: res.messageId,
          suggestions: res.suggestions,
        });

        // 4) Aggiorna dify_conversation_id se nuovo, altrimenti touch updated_at
        if (res.difyConversationId && res.difyConversationId !== conv.dify_conversation_id) {
          await updateDifyConversationId(conv.id, res.difyConversationId);
          setConversation((prev) =>
            prev ? { ...prev, dify_conversation_id: res.difyConversationId! } : prev,
          );
        } else {
          void touch(conv.id);
        }

        // 5) Refresh messaggi da DB + clear streaming
        await qc.invalidateQueries({ queryKey: messagesKey(conv.id) });
        setStreamingMsg(null);
        setStreamingConvId(null);
      } catch (err) {
        // Annullato da noi (nuova chat, uscita dalla pagina): nessun messaggio
        // di errore, l'utente ha gia' ottenuto quello che voleva.
        if ((err as Error)?.name === "AbortError" || controllore.signal.aborted) {
          setStreamingMsg(null);
          setStreamingConvId(null);
          return;
        }
        console.error("[Chat.send] error", err);
        // La quota finita non e' un guasto: e' il momento in cui l'app chiede il
        // codice. Va scritta in chat come una risposta, non come un errore
        // tecnico, perche' e' l'unica occasione che abbiamo per spiegarci.
        // La prova scaduta non scrive in chat: apre il popup, che e' il posto
        // dove questa conversazione commerciale deve avvenire.
        // Sessione scaduta mentre si scriveva: si dice cosa fare, invece di
        // lasciare un errore tecnico in chat.
        if (err instanceof ServeAccount) {
          toast.error("Sessione scaduta", { description: "Accedi di nuovo per continuare." });
          navigate("/login", { replace: true });
          setStreamingMsg(null);
          setStreamingConvId(null);
          return;
        }
        if (err instanceof ProvaScaduta) {
          setPopupScaduto(true);
          setStreamingMsg(null);
          return;
        }
        // La partita IVA c'e' ma non risulta valida: NON e' un guasto
        // passeggero (fino all'08/09 si finiva nel ramo del motore e l'app
        // diceva «riprova fra qualche istante», per sempre). Si porta alle
        // impostazioni, dove il numero si corregge.
        if (err instanceof PartitaIvaNonValida) {
          toast.error("Partita IVA da correggere", { description: err.message, duration: 10000 });
          navigate("/settings", { replace: true });
          setStreamingMsg(null);
          setStreamingConvId(null);
          return;
        }
        // Manca la partita IVA: si va al modulo, come fa la guardia delle rotte.
        if (err instanceof ProfiloIncompleto) {
          toast.error("Completa il profilo", { description: err.message });
          navigate("/completa-profilo", { replace: true });
          setStreamingMsg(null);
          setStreamingConvId(null);
          return;
        }
        // Il controllo dell'account non e' riuscito: si dice con le parole del
        // server, senza il banner «motore non disponibile» che non c'entra.
        if (err instanceof ControlloNonRiuscito) {
          toast.error("Un momento", { description: err.message });
          setStreamingMsg(null);
          setStreamingConvId(null);
          return;
        }
        const quota = err instanceof QuotaEsaurita ? err : null;
        if (conv) {
          try {
            await insertAssistantMessage(conv.id, {
              content: quota
                ? testoQuotaEsaurita(quota.message, Boolean(profile))
                : "Si è verificato un problema temporaneo. Riprova tra qualche istante.",
              status: quota ? "ok" : "degraded",
            });
            await qc.invalidateQueries({ queryKey: messagesKey(conv.id) });
          } catch {
            /* ignore */
          }
        }
        setStreamingMsg(null);
        setStreamingConvId(null);
      } finally {
        if (invioRef.current === controllore) invioRef.current = null;
        setLoading(false);
        sendingRef.current = false;
      }
    },
    [conversation, dbMessages, create, demoMode, navigate, qc, updateDifyConversationId, touch, maybeAutoTitle, insertUserMessage, insertAssistantMessage, image, imagePreviewUrl, profile, user?.id],
  );

  const isEmpty = messages.length === 0 && !loading;
  /** Foglio pulito richiesto dal +, invece della schermata iniziale. */
  const chatNuova = search.get("new") === "1";
  /** Sulla schermata iniziale il tasto Home non serve: ci sei gia'. */
  const sullaHome = !params.id && !chatNuova;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <BloccoProvaScaduta aperto={popupScaduto} onChiudi={() => setPopupScaduto(false)} stato={accountStato} />
      <ConsensoAI
        aperto={chiediConsenso}
        onAccetta={() => {
          registraConsensoAi(user?.id);
          setConsensoAi(true);
          setChiediConsenso(false);
        }}
        onRifiuta={() => setChiediConsenso(false)}
      />
      {/* Header — flusso normale, non fixed: occupa spazio reale */}
      <header className="header-safe flex min-h-[56px] flex-nowrap items-center justify-between gap-2 border-b border-border/60 bg-background/95 pb-2.5 backdrop-blur">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Apri menu"
            className="h-11 w-11 shrink-0"
          >
            <Menu className="h-5 w-5" />
          </Button>
          {/* Solo il marchio e "AI": la parola "Datacom" rubava lo spazio del
              bottone Strumenti (Davide, 26/08). Il nome intero vive nel menu. */}
          <Logo size="sm" />
          <span className="shrink-0 truncate text-[17px] font-semibold tracking-tight text-foreground">
            AI
          </span>
        </div>
        <div className="hidden min-w-0 flex-1 items-center gap-2 lg:flex">
          <span className="truncate text-sm font-medium text-muted-foreground">
            {conversation?.title ?? "Nuova chat"}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Gli strumenti col loro nome, non un'icona da indovinare dentro un
              cerchio (Davide, 26/08). Lo spazio arriva dal titolo accorciato
              e dall'utente spostato nel menu laterale. */}
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-10 shrink-0 gap-1.5 rounded-full border-border/70 bg-surface/60 px-3.5 text-[13px] font-medium text-foreground hover:border-primary/50 hover:text-primary lg:hidden"
          >
            <Link to="/strumenti">
              <Wrench className="h-4 w-4 text-primary" />
              Strumenti
            </Link>
          </Button>
          {/* La via di ritorno alla schermata iniziale (Davide, 27/08). Prima
              non c'era: dentro una conversazione l'unico modo di rivedere le
              comunicazioni di Datacom era il menu laterale. Non compare quando
              sei gia' li': un bottone che non porta da nessuna parte occupa
              spazio e basta. */}
          {!sullaHome && (
            <>
              <Button
                asChild
                variant="outline"
                size="icon"
                aria-label="Schermata iniziale"
                title="Schermata iniziale"
                className="h-10 w-10 shrink-0 rounded-full border-border/70 bg-surface/60 text-foreground hover:border-primary/50 hover:text-primary lg:hidden"
              >
                <Link to="/chat">
                  <House className="h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden gap-1.5 text-xs lg:inline-flex"
              >
                <Link to="/chat">
                  <House className="h-4 w-4" />
                  <span>Home</span>
                </Link>
              </Button>
            </>
          )}
          {/* Icona sola, non il pillolone bianco: il gesto e' frequente ma il
              posto d'onore visivo spetta ai contenuti, non a un bottone. */}
          <Button
            variant="outline"
            size="icon"
            onClick={startNewChat}
            aria-label="Nuova chat"
            className="h-10 w-10 shrink-0 rounded-full border-border/70 bg-surface/60 text-foreground hover:border-primary/50 hover:text-primary lg:hidden"
          >
            <Plus className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={startNewChat}
            className="hidden gap-1.5 text-xs lg:inline-flex"
            aria-label="Nuova chat"
          >
            <Plus className="h-4 w-4" />
            <span>Nuova chat</span>
          </Button>
        </div>
      </header>

      <MobileSidebarDrawer open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen} />

      {/* I banner vivono SOTTO la testata: prima il BannerProva stava sopra,
          a filo dello schermo, e sull'iPhone finiva sotto la Dynamic Island. */}
      <BannerProva stato={accountStato} />
      <DegradedBanner />

      {/* Body scrollabile */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {isEmpty ? (
          /* SUL TELEFONO LA SCHERMATA INIZIALE STA IN BASSO (22/09/2026, dopo
             la prova di Giacomo dell'11/09: «resta una parte vuota»). Era
             ancorata in alto e il vuoto finiva fra gli strumenti e la barra,
             dove l'occhio lo legge come una mancanza. Ancorata in basso, gli
             strumenti stanno a un pollice dalla barra e il vuoto va sotto la
             testata: come una chat, che cresce dal basso. Sui monitor resta il
             centraggio. */
          /* MA NON CON `items-end` (Davide dall'iPhone 15, 28/09: «Come posso
             aiutare» tagliato in cima, e non si poteva scorrere per vederlo).
             In un contenitore che scorre, allineare in basso manda VERSO L'ALTO
             quello che non ci sta - e la' lo scorrimento non arriva: e' il
             difetto noto dell'allineamento flex con overflow. Con la card «Da
             Datacom» il contenuto supera lo schermo, e il titolo spariva.
             I margini automatici fanno la stessa cosa senza quel difetto: se il
             contenuto e' piu' corto spingono in basso (sul telefono) o al centro
             (sui monitor); se e' piu' lungo valgono zero e tutto si scorre. */
          <div className="flex flex-1 flex-col overflow-y-auto pb-1 sm:pb-0">
            <div className="mt-auto flex w-full justify-center sm:mb-auto">
              {chatNuova ? (
                <ChatPulita />
              ) : (
                <ChatLanding
                  onPick={(p) => send(p)}
                  ultima={ultimaDaRiprendere}
                  onRiprendi={(id) => navigate(`/chat/${id}`)}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]">
            <MessageList
              messages={messages}
              loading={loading}
              onPickSuggestion={(s, opts) => {
                if (opts?.isClarification) {
                  // Recupera l'ultima domanda utente per comporre una query completa
                  // (es. "Qual è la soglia... ? Modello: D7021"), così il motore non
                  // ripete la richiesta di chiarimento.
                   const msgs = messages;
                  const lastUser = [...msgs].reverse().find((m) => m.role === "user");
                  const original = lastUser?.content?.trim();
                  const composed = original
                    ? `${original}${/[?.!]$/.test(original) ? "" : "?"} Modello: ${s}`
                    : s;
                  send(composed);
                } else {
                  send(s);
                }
              }}
            />
            {/* L'ultimo salvagente: la riga rossa verso i tecnici Datacom,
                solo quando la regola (lib/salvagente) dice di si'. */}
            <Salvagente conversationId={conversationId} messages={messages} loading={loading} />
          </div>
        )}
      </div>

      {/* Input area (sempre visibile).
          LO SPAZIO IN BASSO, MISURATO INVECE CHE REGALATO (26/08/2026).
          Davide: "lo spazio in basso non lo voglio vedere ne' in app ne' in pwa
          ne' in android, mi pare un bel errore di layout". Aveva ragione: sotto
          l'avvertenza restavano 42 pixel di niente (i 34 della home bar piu' 8
          di respiro), e un vuoto cosi' sotto una riga di testo legge come una
          pagina venuta male.
          Quei 34 pixel pero' non sono decorazione: sono la zona dove iPhone
          disegna la lineetta e intercetta le strisciate. La regola vera e' che
          li' non devono finirci i COMANDI - il campo di scrittura, i pulsanti -
          non che debbano restare vuoti. Il campo resta sopra; l'avvertenza, che
          non si tocca, scende dentro la fascia, e sotto di lei restano i 18
          pixel che servono alla lineetta per non attraversare le parole.
          Sul computer (fascia = 0) si torna agli 8 di sempre. Sull'app
          Android (24/09) la fascia sono i tre tasti di sistema e resta
          intera: il calcolo sta in --fondo-chat (index.css). */}
      <div
        className="border-t border-border/40 bg-background/95 px-0 pt-3 backdrop-blur transition-[padding] duration-200"
        style={{
          paddingBottom: keyboardOpen ? "8px" : "var(--fondo-chat, 8px)",
        }}
      >
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={() => send(input)}
          disabled={loading || bloccata || senzaConsenso}
          image={image}
          imagePreviewUrl={imagePreviewUrl}
          onImageChange={handleImageChange}
        />
        {/* L'avvertenza sta QUI, una volta sola: prima era ripetuta in coda a
            ogni risposta e mangiava due righe di schermo a messaggio. */}
        {senzaConsenso ? (
          <p className="mx-auto max-w-3xl px-4 pt-1.5 text-center text-xs text-muted-foreground">
            L&apos;assistente si attiva col tuo permesso.{" "}
            <button
              type="button"
              onClick={() => setChiediConsenso(true)}
              className="min-h-11 font-medium text-primary underline underline-offset-2"
            >
              Rivedi
            </button>
          </p>
        ) : (
          <p className="mx-auto max-w-3xl px-4 pt-1.5 text-center text-xs text-muted-foreground/70">
            L&apos;AI può sbagliare: verifica sempre sui manuali ufficiali.
          </p>
        )}
        <InstallPromptBanner />
      </div>
    </div>
  );
}